import { Checkbox } from '@timelyapp/react';
import classNames from 'classnames';
import useScrollShadows from 'hooks/useScrollShadows';
import type * as mdast from 'mdast';
import React, { Suspense, useCallback, useContext, useMemo } from 'react';
import { grayscale as syntaxTheme } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import rehypeReact from 'rehype-react';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import remarkStringify from 'remark-stringify';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import styles from './RenderedMarkdown.module.scss';

const SyntaxHighlighter = React.lazy(() => import('react-syntax-highlighter'));

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  text: string | null | undefined;
  maxHeight?: string | number;
  /**
   * Markdown can change from view mode when the user checks or unchecks a checkbox.
   */
  onChangeMarkdown?: (newMarkdown: string) => void;
  withScrollBars?: boolean;
}

const CheckboxContext = React.createContext<{
  handleToggleCheckbox?: (checked: boolean, event: React.SyntheticEvent) => void;
}>({});
CheckboxContext.displayName = 'CheckboxContext';

const RenderedMarkdown = ({
  text,
  maxHeight,
  onChangeMarkdown,
  withScrollBars = true,
  ...props
}: Props) => {
  const scrollShadows = useScrollShadows();
  const rootRef = scrollShadows.scrollRef;
  const markdown = text ?? '';

  const handleToggleCheckbox = useCallback(
    (checked: boolean, event: React.SyntheticEvent) => {
      // Step one: find all checkboxes in the rendered markdown to
      // find out the index of the changed checkbox
      const checkboxElements = [
        ...(rootRef.current?.querySelectorAll(`.${styles.checkbox} input[type="checkbox"]`) ?? []),
      ];
      const checkboxIndex = checkboxElements.indexOf(event.currentTarget);

      // Step two: find the text position of all checkboxes in the markdown AST
      const ast = markdownReactCompiler.parse(markdown);
      const checkboxesInAst: Array<{ startOffset: number; endOffset: number; checked: boolean }> =
        [];
      const traverseAst = (node: mdast.Content) => {
        if (node.type === 'list' || node.type === 'paragraph') {
          node.children.forEach(traverseAst);
        }
        if (node.type === 'listItem') {
          if (node.checked != null) {
            checkboxesInAst.push({
              startOffset: node.position?.start.offset ?? 0,
              endOffset: node.position?.end.offset ?? 0,
              checked: node.checked,
            });
          }
          node.children.forEach(traverseAst);
        }
      };
      (ast.children as any).forEach(traverseAst);

      // If there are not the same number of checkboxes in the markdown and DOM,
      // we can't be sure we can update the right checkbox. In that case, bail
      if (checkboxesInAst.length !== checkboxElements.length) return;

      // Step three: string substitution to update the markdown text
      const checkboxInMarkdown = checkboxesInAst[checkboxIndex];
      const checkboxListItemString = markdown.slice(
        checkboxInMarkdown.startOffset,
        checkboxInMarkdown.endOffset
      );
      const boxStartOffset = checkboxListItemString.indexOf('[') + checkboxInMarkdown.startOffset;
      const boxEndOffset = checkboxListItemString.indexOf(']') + checkboxInMarkdown.startOffset;
      const newMarkdown =
        markdown.slice(0, boxStartOffset + 1) +
        (checked ? 'x' : ' ') +
        markdown.slice(boxEndOffset);
      onChangeMarkdown?.(newMarkdown);
    },
    [rootRef, markdown, onChangeMarkdown]
  );

  const html = useMemo(() => {
    return markdownReactCompiler.processSync(markdown).result as any;
  }, [markdown]);

  if (!withScrollBars) {
    return (
      <div className={styles.markdown}>
        <CheckboxContext.Provider value={{ handleToggleCheckbox }}>{html}</CheckboxContext.Provider>
      </div>
    );
  }

  return (
    <CheckboxContext.Provider value={{ handleToggleCheckbox }}>
      <div className={styles.boxWrapper}>
        {scrollShadows.shadows}
        <div
          ref={scrollShadows.scrollRef}
          className={classNames(styles.box, styles.markdown, {})}
          onScroll={scrollShadows.onScroll}
          {...props}
          style={{
            maxHeight,
            ...props.style,
          }}
        >
          {html}
        </div>
      </div>
    </CheckboxContext.Provider>
  );
};

export default RenderedMarkdown;

const languageAliases: Record<string, string> = {
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  json: 'javascript',
  md: 'markdown',
  html: 'markdown',
};
const CodeBlock: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const content = Array.isArray(children) ? children[0] : children;
  if (React.isValidElement<InlineCodeProps>(content) && content.type === InlineCode) {
    let { children: code } = content.props;
    if (Array.isArray(code) && typeof code[0] === 'string') {
      code = code[0].trim();
    }
    let language = content.props.className?.replace('language-', '') ?? 'text';
    language = languageAliases[language] ?? language;
    return (
      <pre>
        <Suspense fallback={<pre style={{ padding: '0.5em' }}>{code}</pre>}>
          <SyntaxHighlighter language={language} style={syntaxTheme}>
            {code}
          </SyntaxHighlighter>
        </Suspense>
      </pre>
    );
  }
  return <pre>{content}</pre>;
};
interface InlineCodeProps extends React.HTMLAttributes<HTMLElement> {}
const InlineCode: React.FC<InlineCodeProps> = ({ children, ...props }) => {
  return (
    <code {...props} className={styles.inlineCode}>
      {children}
    </code>
  );
};
const Link: React.FC<React.HTMLAttributes<HTMLAnchorElement>> = ({ children, ...props }) => {
  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const Input: React.FC<React.HTMLProps<HTMLInputElement>> = props => {
  const checkboxContext = useContext(CheckboxContext);
  if (props.type === 'checkbox') {
    return (
      <Checkbox
        checked={props.checked}
        onChangeChecked={checkboxContext.handleToggleCheckbox}
        className={styles.checkbox}
      />
    );
  }
  return <input {...props} />;
};

const createBaseCompiler = () => unified().use(remarkParse).use(remarkGfm);
const markdownReactCompiler = createBaseCompiler()
  .use(remarkRehype)
  .use(rehypeReact, {
    createElement: React.createElement,
    Fragment: React.Fragment,
    components: {
      pre: CodeBlock,
      code: InlineCode,
      a: Link,
      input: Input,
    },
  })
  .freeze();

const markdownPlainTextCompiler = createBaseCompiler()
  .use(() => {
    return (tree: mdast.Root) => {
      // Custom plain text plugin since nothing else was good enough
      visit(tree, (node: any, _, parent) => {
        if (node.type === 'html') {
          node.value = '';
          (node as any).type = 'paragraph';
          return 'skip';
        }
        if (['image', 'imageReference', 'definition', 'thematicBreak'].includes(node.type)) {
          node.type = 'paragraph';
          return 'skip';
        }
        if (node.type === 'text' && node.value === parent?.url) {
          node.value = String(node.value).replace(/https?:\/\//, '');
          (node as any).type = 'html';
          return 'skip';
        }
        if (node.type === 'listItem') {
          const label = (node.children as any)?.[0]?.children?.[0];
          if (label) {
            label.value = `• ${label.value}`;
          }
        }

        if (node.type === 'text') {
          node.value += '\n';
        }
        if (node.type !== 'text' && Array.isArray(node.children)) {
          node.type = 'paragraph';
        }
      });
    };
  })
  .use(remarkStringify)
  .freeze();

interface MarkdownAsPlainTextProps {
  markdown: string;
  // Truncate the formatting-stripped text to a set number of characters
  truncate?: number;
}
export const MarkdownAsPlainText = ({ markdown, truncate }: MarkdownAsPlainTextProps) => {
  const text = useMemo(() => renderMarkdownToPlainText(markdown), [markdown]);

  const truncatedText = useMemo(() => {
    if (truncate != null) {
      const truncated = text.slice(0, truncate);
      if (truncated.length < text.length) {
        return truncated.slice(0, truncate - 1) + '…';
      }
    }
    return text;
  }, [text, truncate]);

  return <>{truncatedText}</>;
};

export const renderMarkdownToPlainText = (markdown: string) => {
  const plainText = String(markdownPlainTextCompiler.processSync(markdown).value);
  // Remove some junk
  return plainText.replace(/&#x20;/gi, ' ');
};
